import { useSelector } from "react-redux";
import { airlinesSelector } from "@b2bportal/core-flights";
import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  type IFlightNumberFilter,
  type IFlightNumbersByAirlineCode,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { CheckboxGroup } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./FilterFlightNumber.module.scss";

export interface FilterFlightNumberProps extends PlatformComponentProps {
  selectedFlightNumber: IFlightNumberFilter[];
  flightNumbersByAirline: IFlightNumbersByAirlineCode;
  onFlightNumberChange: (val: IFlightNumberFilter[]) => void;
}

export const FilterFlightNumber = ({
  selectedFlightNumber,
  flightNumbersByAirline,
  className,
  onFlightNumberChange,
}: FilterFlightNumberProps) => {
  const COMPONENT_KEY = CoreFlightsComponent.FilterFlightNumber;
  const styles = useFlightStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const airlines = useSelector(airlinesSelector);

  const airlineGroup = Object.keys(flightNumbersByAirline)?.map((o) => ({
    label: airlines?.[o].displayName,
    value: o,
  }));

  const handleAirlineCheckboxChange = (
    airlineCode: string,
    isChecked: boolean
  ) => {
    if (isChecked) {
      // Select all flight numbers for this airline
      const allFlightNumbersForAirline = flightNumbersByAirline[
        airlineCode
      ]?.map((flightNumber) => ({
        airlineCode,
        flightNumber,
      }));

      // Keep selections from other airlines and add all from this airline
      const otherAirlineSelections = selectedFlightNumber.filter(
        (f) => f.airlineCode !== airlineCode
      );

      onFlightNumberChange([
        ...otherAirlineSelections,
        ...(allFlightNumbersForAirline || []),
      ]);
    } else {
      // Deselect all flight numbers for this airline
      const filteredFlightNumbers = selectedFlightNumber.filter(
        (f) => f.airlineCode !== airlineCode
      );
      onFlightNumberChange(filteredFlightNumbers);
    }
  };

  return (
    <div className={clsx(block, className)}>
      {airlineGroup.map((airline) => {
        // Check if any flight numbers are selected for this airline
        const hasSelectedFlights = selectedFlightNumber.some(
          (f) => f.airlineCode === airline.value
        );
        const isExpanded = hasSelectedFlights;

        const flightNumbers = flightNumbersByAirline[airline.value]?.map(
          (n) => ({
            label: `${airline.value} ${n}`,
            value: `${airline.value} ${n}`,
          })
        );

        return (
          <div key={airline.value} className={cn("airline-group")}>
            <CheckboxGroup
              options={[airline]}
              checkedOptions={hasSelectedFlights ? [airline.value] : []}
              onOptionsChange={(values) => {
                const isChecked = values.includes(airline.value);
                handleAirlineCheckboxChange(airline.value, isChecked);
              }}
              className={cn("airline")}
            />
            {isExpanded && flightNumbers && flightNumbers.length > 0 && (
              <CheckboxGroup
                options={flightNumbers}
                checkedOptions={selectedFlightNumber.map(
                  (f) => `${f.airlineCode} ${f.flightNumber}`
                )}
                onOptionsChange={(val) => {
                  // Keep selections from other airlines
                  const otherAirlineSelections = selectedFlightNumber.filter(
                    (f) => f.airlineCode !== airline.value
                  );

                  // Parse the new selections for this airline
                  const newAirlineSelections = val.map((v) => {
                    const spaceIndex = v.indexOf(" ");
                    const airlineCode = v.substring(0, spaceIndex);
                    const flightNumber = v.substring(spaceIndex + 1);
                    return {
                      airlineCode,
                      flightNumber,
                    };
                  });

                  onFlightNumberChange([
                    ...otherAirlineSelections,
                    ...newAirlineSelections,
                  ]);
                }}
                className={cn("flight-numbers")}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
